import React from 'react';
import { Lightbulb, Heart, Grid3x3 } from 'lucide-react';
import type { Language } from '../types';

interface StoriesBarProps {
  categories: string[];
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
  showLikedFilter?: boolean;
  isLikedFilterActive?: boolean;
  onToggleLikedFilter?: () => void;
  hasUnviewedGuide?: boolean;
  onOpenGuide?: () => void;
  language: Language;
}

const TRANSLATIONS = {
  Italian: {
    all: 'Tutti',
    liked: 'Preferiti',
    guide: 'Guida',
  },
  English: {
    all: 'All',
    liked: 'Favorites',
    guide: 'Guide',
  },
  Spanish: {
    all: 'Todos',
    liked: 'Favoritos',
    guide: 'Guía',
  },
  German: {
    all: 'Alle',
    liked: 'Favoriten',
    guide: 'Anleitung',
  },
  French: {
    all: 'Tous',
    liked: 'Favoris',
    guide: 'Guide',
  },
};

// Category to emoji mapping (all supported languages)
const CATEGORY_EMOJI: Record<string, string> = {
  // Health - English, Italian, Spanish, German, French
  Health: '🏃‍♂️',
  Salute: '🏃‍♂️',
  Salud: '🏃‍♂️',
  Gesundheit: '🏃‍♂️',
  Santé: '🏃‍♂️',
  // Focus - English, Italian, Spanish, German, French
  Focus: '🧠',
  Enfoque: '🧠',
  Fokus: '🧠',
  // Connection - English, Italian, Spanish, German, French
  Connection: '💬',
  Connessione: '💬',
  Conexión: '💬',
  Verbindung: '💬',
  Connexion: '💬',
  // Spirit - English, Italian, Spanish, German, French
  Spirit: '✨',
  Spirito: '✨',
  Espíritu: '✨',
  Geist: '✨',
  Esprit: '✨',
  // Finance - English, Italian, Spanish, German, French
  Finance: '💰',
  Finanza: '💰',
  Finanzas: '💰',
  Finanzen: '💰',
  // Memory - English, Italian, Spanish, German, French
  Memory: '📸',
  Memoria: '📸',
  Erinnerung: '📸',
  Mémoire: '📸',
  // Note - English, Italian, Spanish, German, French
  Note: '📝',
  Nota: '📝',
  Notiz: '📝',
};

// Category gradient mapping (all supported languages)
const CATEGORY_GRADIENTS: Record<string, string> = {
  // Health
  Health: 'from-rose-400 to-pink-500',
  Salute: 'from-rose-400 to-pink-500',
  Salud: 'from-rose-400 to-pink-500',
  Gesundheit: 'from-rose-400 to-pink-500',
  Santé: 'from-rose-400 to-pink-500',
  // Focus
  Focus: 'from-amber-400 to-orange-500',
  Enfoque: 'from-amber-400 to-orange-500',
  Fokus: 'from-amber-400 to-orange-500',
  // Connection
  Connection: 'from-blue-400 to-indigo-500',
  Connessione: 'from-blue-400 to-indigo-500',
  Conexión: 'from-blue-400 to-indigo-500',
  Verbindung: 'from-blue-400 to-indigo-500',
  Connexion: 'from-blue-400 to-indigo-500',
  // Spirit
  Spirit: 'from-purple-400 to-violet-500',
  Spirito: 'from-purple-400 to-violet-500',
  Espíritu: 'from-purple-400 to-violet-500',
  Geist: 'from-purple-400 to-violet-500',
  Esprit: 'from-purple-400 to-violet-500',
  // Finance
  Finance: 'from-emerald-400 to-teal-500',
  Finanza: 'from-emerald-400 to-teal-500',
  Finanzas: 'from-emerald-400 to-teal-500',
  Finanzen: 'from-emerald-400 to-teal-500',
  // Memory
  Memory: 'from-cyan-400 to-blue-500',
  Memoria: 'from-cyan-400 to-blue-500',
  Erinnerung: 'from-cyan-400 to-blue-500',
  Mémoire: 'from-cyan-400 to-blue-500',
  // Note
  Note: 'from-gray-400 to-slate-500',
  Nota: 'from-gray-400 to-slate-500',
  Notiz: 'from-gray-400 to-slate-500',
};

const StoriesBar: React.FC<StoriesBarProps> = ({
  categories,
  selectedCategory,
  onSelectCategory,
  showLikedFilter = true,
  isLikedFilterActive = false,
  onToggleLikedFilter,
  hasUnviewedGuide = false,
  onOpenGuide,
  language,
}) => {
  const t = TRANSLATIONS[language] || TRANSLATIONS.English;

  return (
    <div className="w-full bg-white border-b border-black/5">
      <div className="flex items-center gap-3 px-4 py-3 overflow-x-auto no-scrollbar">
        {/* Daily Guide Story */}
        {onOpenGuide && (
          <button
            onClick={onOpenGuide}
            className="flex flex-col items-center gap-1 shrink-0"
          >
            <div className={`relative w-[62px] h-[62px] rounded-full p-[2px] ${
              hasUnviewedGuide
                ? 'bg-gradient-to-tr from-purple-500 via-pink-500 to-orange-400'
                : 'bg-gray-200'
            }`}>
              <div className="w-full h-full rounded-full bg-white p-[2px]">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
                  <Lightbulb size={24} className="text-white" fill="white" />
                </div>
              </div>
              {hasUnviewedGuide && (
                <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded text-[8px] font-bold text-white uppercase tracking-wide">
                  New
                </div>
              )}
            </div>
            <span className="text-[11px] font-medium text-black/70 truncate max-w-[64px]">
              {t.guide}
            </span>
          </button>
        )}

        {/* Liked/Favorites Filter */}
        {showLikedFilter && onToggleLikedFilter && (
          <button
            onClick={onToggleLikedFilter}
            className="flex flex-col items-center gap-1 shrink-0"
          >
            <div className={`relative w-[62px] h-[62px] rounded-full p-[2px] ${
              isLikedFilterActive
                ? 'bg-gradient-to-tr from-rose-500 to-pink-500'
                : 'bg-gray-200'
            }`}>
              <div className="w-full h-full rounded-full bg-white p-[2px]">
                <div className={`w-full h-full rounded-full flex items-center justify-center ${
                  isLikedFilterActive
                    ? 'bg-gradient-to-br from-rose-500 to-pink-500'
                    : 'bg-gray-100'
                }`}>
                  <Heart
                    size={24}
                    className={isLikedFilterActive ? 'text-white' : 'text-gray-400'}
                    fill={isLikedFilterActive ? 'currentColor' : 'none'}
                  />
                </div>
              </div>
            </div>
            <span className={`text-[11px] font-medium truncate max-w-[64px] ${
              isLikedFilterActive ? 'text-rose-500' : 'text-black/70'
            }`}>
              {t.liked}
            </span>
          </button>
        )}

        {/* All Categories */}
        <button
          onClick={() => onSelectCategory(null)}
          className="flex flex-col items-center gap-1 shrink-0"
        >
          <div className={`relative w-[62px] h-[62px] rounded-full p-[2px] ${
            selectedCategory === null && !isLikedFilterActive
              ? 'bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500'
              : 'bg-gray-200'
          }`}>
            <div className="w-full h-full rounded-full bg-white p-[2px]">
              <div className={`w-full h-full rounded-full flex items-center justify-center ${
                selectedCategory === null && !isLikedFilterActive
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                  : 'bg-gray-100'
              }`}>
                <Grid3x3
                  size={24}
                  className={selectedCategory === null && !isLikedFilterActive ? 'text-white' : 'text-gray-400'}
                />
              </div>
            </div>
          </div>
          <span className={`text-[11px] font-medium truncate max-w-[64px] ${
            selectedCategory === null && !isLikedFilterActive ? 'text-indigo-600' : 'text-black/70'
          }`}>
            {t.all}
          </span>
        </button>

        {/* Category Stories */}
        {categories.map((category) => {
          const isSelected = selectedCategory === category && !isLikedFilterActive;
          const emoji = CATEGORY_EMOJI[category] || '📌';
          const gradient = CATEGORY_GRADIENTS[category] || 'from-gray-400 to-slate-500';

          return (
            <button
              key={category}
              onClick={() => onSelectCategory(category)}
              className="flex flex-col items-center gap-1 shrink-0"
            >
              <div className={`relative w-[62px] h-[62px] rounded-full p-[2px] ${
                isSelected
                  ? `bg-gradient-to-tr ${gradient}`
                  : 'bg-gray-200'
              }`}>
                <div className="w-full h-full rounded-full bg-white p-[2px]">
                  <div className={`w-full h-full rounded-full flex items-center justify-center ${
                    isSelected
                      ? `bg-gradient-to-br ${gradient}`
                      : 'bg-gray-50'
                  }`}>
                    <span className="text-2xl">{emoji}</span>
                  </div>
                </div>
              </div>
              <span className={`text-[11px] font-medium truncate max-w-[64px] ${
                isSelected ? 'text-black' : 'text-black/70'
              }`}>
                {category}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StoriesBar;
